/**
 * PipelineOrchestratorService
 *
 * Concrete implementation of IPipelineOrchestrator
 * Manages pipeline execution and hook lifecycle
 *
 * Single Responsibility: Pipeline execution and metrics
 */

import type { IPipelineOrchestrator, PipelineExecutionResult, PipelineExecutionMetrics, HookExecutionMetrics } from '../interfaces';
import type { IEnvelope } from '../../types';
import type { ILoggingStrategy } from '../interfaces';

/**
 * Mock IncomingPipeline - will be replaced by actual EdgeStream pipeline
 */
interface IIncomingPipeline {
  execute(context: { envelope: IEnvelope; serverId: string }): Promise<void>;
  getHooks?(): Array<{ name: string; priority: number }>;
}

export class PipelineOrchestratorService implements IPipelineOrchestrator {
  private executionHistory: Map<string, PipelineExecutionMetrics> = new Map();
  private stats = {
    total: 0,
    success: 0,
    error: 0,
    totalDurationMs: 0,
    slowestHook: '',
    slowestDurationMs: 0,
    criticalErrors: [] as Error[],
  };

  constructor(
    private incomingPipeline: IIncomingPipeline,
    private outgoingPipeline: IIncomingPipeline,
    private logger?: ILoggingStrategy
  ) {}

  async executeIncoming(envelope: IEnvelope, serverId: string): Promise<PipelineExecutionResult> {
    return this.executePipeline('incoming', this.incomingPipeline, envelope, serverId);
  }

  async executeOutgoing(envelope: IEnvelope, serverId: string): Promise<PipelineExecutionResult> {
    return this.executePipeline('outgoing', this.outgoingPipeline, envelope, serverId);
  }

  getHooks(direction: 'incoming' | 'outgoing') {
    const pipeline = direction === 'incoming' ? this.incomingPipeline : this.outgoingPipeline;

    if (!pipeline.getHooks) return [];

    return pipeline.getHooks().map((h) => ({
      name: h.name,
      priority: h.priority,
      description: `Hook: ${h.name}`,
    }));
  }

  getStatistics() {
    return {
      totalExecutions: this.stats.total,
      successCount: this.stats.success,
      errorCount: this.stats.error,
      averageDurationMs: this.stats.total > 0 ? this.stats.totalDurationMs / this.stats.total : 0,
      slowestHook: this.stats.slowestHook,
      slowestDurationMs: this.stats.slowestDurationMs,
      criticalErrors: this.stats.criticalErrors,
    };
  }

  resetStatistics(): void {
    this.stats = {
      total: 0,
      success: 0,
      error: 0,
      totalDurationMs: 0,
      slowestHook: '',
      slowestDurationMs: 0,
      criticalErrors: [],
    };
  }

  getExecutionHistory(messageId: string): PipelineExecutionMetrics | undefined {
    return this.executionHistory.get(messageId);
  }

  isHealthy(): boolean {
    // Simple health check: no critical errors in last 10 executions
    return this.stats.criticalErrors.length < 5;
  }

  // Private methods

  private async executePipeline(
    direction: 'incoming' | 'outgoing',
    pipeline: IIncomingPipeline,
    envelope: IEnvelope,
    serverId: string
  ): Promise<PipelineExecutionResult> {
    const messageId = envelope.meta.id as string;
    const startTime = Date.now();
    const hookMetrics: HookExecutionMetrics[] = [];
    const errors: Error[] = [];
    const warnings: string[] = [];

    try {
      // Log pipeline start
      if (this.logger) {
        const ctx = this.logger.createContext(messageId);
        this.logger.logMessageLifecycle(messageId, 'pipeline-start', ctx, { direction });
      }

      // Execute pipeline
      try {
        await pipeline.execute({ envelope, serverId });
      } catch (error) {
        const errorObj = error instanceof Error ? error : new Error(String(error));
        errors.push(errorObj);

        if (this.logger) {
          const ctx = this.logger.createContext(messageId);
          this.logger.error(`Pipeline ${direction} error`, errorObj, ctx);
        }
      }

      const totalDuration = Date.now() - startTime;

      // Create metrics
      const metrics: PipelineExecutionMetrics = {
        messageId,
        direction,
        totalDuration,
        hookCount: hookMetrics.length,
        hookMetrics,
        status: errors.length === 0 ? 'success' : errors.length === hookMetrics.length ? 'error' : 'partial',
        startTime: new Date(startTime),
        endTime: new Date(),
      };

      // Store history
      this.executionHistory.set(messageId, metrics);

      // Update stats
      this.stats.total++;
      if (metrics.status === 'success') {
        this.stats.success++;
      } else {
        this.stats.error++;
      }
      this.stats.totalDurationMs += totalDuration;

      // Track slow hooks
      for (const hook of hookMetrics) {
        if (hook.duration > this.stats.slowestDurationMs) {
          this.stats.slowestDurationMs = hook.duration;
          this.stats.slowestHook = hook.hookName;
        }
      }

      // Track critical errors
      if (errors.length > 0) {
        this.stats.criticalErrors.push(...errors);
        if (this.stats.criticalErrors.length > 100) {
          this.stats.criticalErrors = this.stats.criticalErrors.slice(-100);
        }
      }

      // Log completion
      if (this.logger) {
        const ctx = this.logger.createContext(messageId);
        this.logger.logMessageLifecycle(messageId, 'pipeline-complete', ctx, {
          direction,
          duration: totalDuration,
          status: metrics.status,
        });
      }

      return {
        success: metrics.status === 'success',
        messageId,
        envelope,
        metrics,
        errors,
        warnings,
      };
    } catch (error) {
      const totalDuration = Date.now() - startTime;
      const errorObj = error instanceof Error ? error : new Error(String(error));

      if (this.logger) {
        const ctx = this.logger.createContext(messageId);
        this.logger.error(`Unexpected pipeline error`, errorObj, ctx);
      }

      this.stats.total++;
      this.stats.error++;
      this.stats.criticalErrors.push(errorObj);

      const metrics: PipelineExecutionMetrics = {
        messageId,
        direction,
        totalDuration,
        hookCount: 0,
        hookMetrics: [],
        status: 'error',
        startTime: new Date(startTime),
        endTime: new Date(),
      };

      this.executionHistory.set(messageId, metrics);

      return {
        success: false,
        messageId,
        envelope,
        metrics,
        errors: [errorObj],
        warnings,
      };
    }
  }
}
